import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Paper, Button as MuiButton, Typography, createTheme, ThemeProvider } from '@mui/material';
import { useThemeContext } from './ThemeWrapper'; // Assuming ThemeWrapper.tsx provides this context

const Whiteboard = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [drawing, setDrawing] = useState(false);
    const navigate = useNavigate();
    const { isDarkTheme } = useThemeContext();

    // Determine drawing color based on theme for contrast against the chalkboard background
    const drawingColor = '#fffbe7'; // Off-white chalk color for drawing

    // Adjust canvas size on mount and window resize for responsiveness
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const setCanvasDimensions = () => {
            // Set canvas's internal dimensions to match its displayed size (offsetWidth/Height)
            // This is crucial for preventing blurry drawings on resolution changes.
            canvas.width = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;

            // Clear and redraw background after resize to prevent drawing distortion
            // The chalkboard green background is consistent regardless of theme
            ctx.fillStyle = '#2d4c2a';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
        };

        // Initial dimension setting
        setCanvasDimensions();
        // Add resize listener to re-adjust canvas dimensions when window size changes
        window.addEventListener('resize', setCanvasDimensions);

        // Cleanup listener on component unmount
        return () => {
            window.removeEventListener('resize', setCanvasDimensions);
        };
    }, []); // Empty dependency array means this effect runs once on mount and cleans up on unmount

    // Prevent touch scroll while drawing to improve mobile experience
    useEffect(() => {
        const preventScroll = (e: TouchEvent) => {
            if (drawing) e.preventDefault();
        };
        document.addEventListener('touchmove', preventScroll, { passive: false });
        return () => {
            document.removeEventListener('touchmove', preventScroll);
        };
    }, [drawing]); // Re-run if 'drawing' state changes

    // Helper to get drawing coordinates from mouse or touch events
    const getCoordinates = (e: React.MouseEvent | React.TouchEvent) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        const rect = canvas.getBoundingClientRect();

        if (e.nativeEvent instanceof TouchEvent) {
            const touch = e.nativeEvent.touches[0];
            return {
                x: touch.clientX - rect.left,
                y: touch.clientY - rect.top
            };
        } else {
            return {
                x: e.nativeEvent.offsetX,
                y: e.nativeEvent.offsetY
            };
        }
    };

    // Start drawing path
    const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
        const { x, y } = getCoordinates(e);
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.beginPath();
        ctx.moveTo(x, y);
        setDrawing(true);
    };

    // Continue drawing path
    const draw = (e: React.MouseEvent | React.TouchEvent) => {
        if (!drawing) return;
        const { x, y } = getCoordinates(e);
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.lineTo(x, y);
        ctx.strokeStyle = drawingColor; // Use the defined chalk color
        ctx.lineWidth = 4;
        ctx.lineCap = 'round';
        ctx.stroke();
    };

    // Stop drawing path
    const stopDrawing = () => setDrawing(false);

    // Clear the canvas and redraw the chalkboard background
    const handleClear = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        // Redraw background after clearing to maintain chalkboard appearance
        ctx.fillStyle = '#2d4c2a';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
    };

    // Save canvas content as an image (PNG)
    const handleSave = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const url = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = 'whiteboard.png';
        link.href = url;
        link.click();
    };

    // Material-UI theme for dark mode compatibility and consistent styling
    const muiTheme = createTheme({
        palette: {
            mode: isDarkTheme ? 'dark' : 'light',
            primary: {
                main: '#FF7F3F', // A vibrant orange for primary actions (Back, Save)
            },
            secondary: {
                main: '#4ade80', // Green for secondary actions (Clear)
            },
            background: {
                default: isDarkTheme ? '#1a202c' : '#f0f4f8', // Overall page background
                paper: isDarkTheme ? '#2d3748' : '#ffffff', // Card background
            },
            text: {
                primary: isDarkTheme ? '#e2e8f0' : '#2d3748',
                secondary: isDarkTheme ? '#a0aec0' : '#718096',
            },
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: '0.75rem', // rounded-xl
                        textTransform: 'none',
                        fontWeight: 600,
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        borderRadius: '1.5rem', // rounded-3xl
                    },
                },
            },
        },
    });

    return (
        <ThemeProvider theme={muiTheme}>
            {/* Main container with wood texture background */}
            <div
                className="fixed inset-0 w-screen h-screen min-h-screen min-w-screen m-0 p-0 text-center flex flex-col items-center justify-center overflow-auto"
                style={{
                    backgroundColor: '#7c5a2a', // Base wood color
                    backgroundImage: `
                        repeating-linear-gradient(90deg, rgba(140, 100, 50, 0.18) 0px, rgba(140, 100, 50, 0.18) 8px, transparent 8px, transparent 24px),
                        repeating-linear-gradient(0deg, rgba(120, 80, 30, 0.12) 0px, rgba(120, 80, 30, 0.12) 2px, transparent 2px, transparent 12px),
                        linear-gradient(120deg, #8d6b36 0%, #a47c48 40%, #7c5a2a 100%)
                    `
                }}
            >
                {/* BACK BUTTON */}
                <motion.div
                    className="absolute top-4 left-4 sm:top-8 sm:left-8 z-10"
                    initial={{ x: -100, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 100, damping: 10 }}
                >
                    <MuiButton
                        variant="contained"
                        color="primary"
                        startIcon={<ArrowLeft size={20} />}
                        onClick={() => navigate("/hiragana")}
                        className="!bg-orange-500 !text-white !font-bold !py-2 !px-4 !rounded-xl !shadow-md
                                   hover:!bg-orange-600 dark:!bg-orange-600 dark:hover:!bg-orange-700"
                        component={motion.button}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        Back
                    </MuiButton>
                </motion.div>

                {/* Main Whiteboard Content Area */}
                <motion.div
                   
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ type: "spring", stiffness: 50, damping: 10, delay: 0.2 }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        className="!font-extrabold text-[#fffbe7] mb-4 mt-0 text-center" // Chalkboard title color
                        sx={{
                            fontFamily: "'Noto Sans JP', 'Yu Mincho', 'Meiryo', serif", // Specific Japanese font
                            letterSpacing: '0.1em',
                            textShadow: '0 2px 8px #1a2a18, 0 1px 0 #fffbe7', // Chalkboard text shadow
                            fontSize: { xs: '1.8rem', sm: '2rem' }, // Responsive font size
                        }}
                    >
                        Practice Board
                    </Typography>
                    {/* Canvas Container with Chalkboard Background and Border */}
                    <div
                        className="relative w-full rounded-2xl overflow-hidden shadow-lg border-[12px] border-[#bfa76f] cursor-crosshair mb-4 transition-shadow
                                   h-[350px] sm:h-[450px] md:h-[500px] lg:h-[600px] xl:h-[700px] max-h-[70vh]" // Responsive height for the canvas container
                        style={{
                            background: `
                                repeating-linear-gradient(to bottom, #2d4c2a 0px, #2d4c2a 39px, #30512d 40px, #2d4c2a 41px),
                                radial-gradient(circle at 30% 30%, rgba(255,255,255,0.04) 0%, transparent 80%),
                                radial-gradient(circle at 70% 70%, rgba(255,255,255,0.03) 0%, transparent 80%)
                            `,
                            boxShadow: '0 8px 32px #1a2a18, 0 1px 0 #bfa76f', // Chalkboard shadow
                        }}
                    >
                        <canvas
                            ref={canvasRef}
                            className="w-full h-full block relative z-0" // Canvas fills its parent container
                            onMouseDown={startDrawing}
                            onMouseMove={draw}
                            onMouseUp={stopDrawing}
                            onMouseLeave={stopDrawing}
                            onTouchStart={startDrawing}
                            onTouchMove={draw}
                            onTouchEnd={stopDrawing}
                        />
                        {/* Overlay for linen texture effect (chalkboard texture) */}
                        <div
                            className="absolute inset-0 pointer-events-none opacity-10 z-10"
                            style={{ backgroundImage: "url('https://www.transparenttextures.com/patterns/black-linen.png')" }}
                        ></div>
                    </div>
                    {/* Control Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4 mt-4 w-full justify-center">
                        <MuiButton
                            variant="contained"
                            color="secondary"
                            onClick={handleClear}
                            className="!bg-[#fffbe7] !text-[#2d4c2a] !font-bold !py-2 !px-4 !rounded-md !shadow-md !border-2 !border-[#bfa76f]
                                       hover:!bg-[#bfa76f] hover:!text-[#fffbe7] dark:!bg-gray-600 dark:!text-gray-200 dark:hover:!bg-gray-500"
                            component={motion.button}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            sx={{
                                '&:hover': {
                                    boxShadow: '0 4px 16px rgba(26, 42, 24, 0.4)', // Matching original hover shadow
                                }
                            }}
                        >
                            Clear
                        </MuiButton>
                        <MuiButton
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                            className="!bg-[#fffbe7] !text-[#2d4c2a] !font-bold !py-2 !px-4 !rounded-md !shadow-md !border-2 !border-[#bfa76f]
                                       hover:!bg-[#bfa76f] hover:!text-[#fffbe7] dark:!bg-gray-600 dark:!text-gray-200 dark:hover:!bg-gray-500"
                            component={motion.button}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            sx={{
                                '&:hover': {
                                    boxShadow: '0 4px 16px rgba(26, 42, 24, 0.4)', // Matching original hover shadow
                                }
                            }}
                        >
                            Save as Image
                        </MuiButton>
                    </div>
                </motion.div>
            </div>
        </ThemeProvider>
    );
};

export default Whiteboard;